import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkIcon from "@mui/icons-material/Link";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useAddServiceBriefingAssetMutation,
  useDeleteServiceBriefingAssetMutation,
} from "../../store/apis/serviceBriefingApi";
import {
  deleteServiceBriefingFile,
  MAX_SERVICE_BRIEFING_FILE_BYTES,
  uploadServiceBriefingFile,
} from "../../firebase/serviceBriefingStorage";
import { ServiceBriefingAssetView } from "../../types/serviceBriefing";
import { formatBytes } from "./serviceBriefingLabels";
import ConfirmationModal from "../modals/ConfirmationModal";

interface Props {
  serviceBriefingId: string;
  assets: ServiceBriefingAssetView[];
}

interface Upload {
  name: string;
  percent: number;
}

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { message?: string } })?.data?.message ??
  (error instanceof Error ? error.message : fallback);

/**
 * Los assets que necesita quien hace el trabajo: ficheros subidos a Storage o enlaces
 * (Drive, WeTransfer, Figma...).
 *
 * El fichero se sube primero y luego se da de alta en el backend. Si el alta falla, se
 * borra lo subido para no dejar ficheros huérfanos en el bucket.
 */
const ServiceBriefingAssets: React.FC<Props> = ({
  serviceBriefingId,
  assets,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [toDelete, setToDelete] = useState<ServiceBriefingAssetView | null>(
    null
  );

  const [addAsset, { isLoading: isAdding }] =
    useAddServiceBriefingAssetMutation();
  const [deleteAsset, { isLoading: isDeleting }] =
    useDeleteServiceBriefingAssetMutation();

  const toast = (
    message: string,
    severity: "success" | "error" | "warning" = "success"
  ) => dispatch(setToastMessage({ message, severity }));

  const uploadOne = async (file: File) => {
    setUploads((current) => [...current, { name: file.name, percent: 0 }]);
    const setPercent = (percent: number) =>
      setUploads((current) =>
        current.map((u) => (u.name === file.name ? { ...u, percent } : u))
      );
    try {
      const uploaded = await uploadServiceBriefingFile(
        file,
        serviceBriefingId,
        setPercent
      );
      try {
        await addAsset({
          id: serviceBriefingId,
          body: {
            kind: "FILE",
            name: file.name,
            url: uploaded.url,
            storagePath: uploaded.storagePath,
            contentType: uploaded.contentType,
            sizeBytes: uploaded.sizeBytes,
          },
        }).unwrap();
      } catch (error) {
        await deleteServiceBriefingFile(uploaded.storagePath).catch(() => {});
        throw error;
      }
      toast(
        t("serviceBriefings.assets.uploaded", "{{name}} subido", {
          name: file.name,
        })
      );
    } catch (error) {
      toast(
        errorMessage(
          error,
          t(
            "serviceBriefings.assets.uploadFailed",
            "No se pudo subir el fichero"
          )
        ),
        "error"
      );
    } finally {
      setUploads((current) => current.filter((u) => u.name !== file.name));
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    // De uno en uno: en paralelo, una conexión lenta reparte el ancho de banda y no
    // termina ninguno.
    for (const file of Array.from(files)) {
      await uploadOne(file);
    }
    if (fileInput.current) fileInput.current.value = "";
  };

  const linkValid = linkName.trim() !== "" && /^https?:\/\//i.test(linkUrl);

  const handleAddLink = async () => {
    try {
      await addAsset({
        id: serviceBriefingId,
        body: { kind: "LINK", name: linkName.trim(), url: linkUrl.trim() },
      }).unwrap();
      setLinkName("");
      setLinkUrl("");
      toast(t("serviceBriefings.assets.linkAdded", "Enlace añadido"));
    } catch (error) {
      toast(
        errorMessage(
          error,
          t("serviceBriefings.assets.linkFailed", "No se pudo añadir el enlace")
        ),
        "error"
      );
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const response = await deleteAsset({
        id: serviceBriefingId,
        assetId: toDelete.id,
      }).unwrap();
      const storagePath = response.data?.storagePath;
      if (storagePath) {
        try {
          await deleteServiceBriefingFile(storagePath);
        } catch {
          toast(
            t(
              "serviceBriefings.assets.fileNotDeleted",
              "Quitado de la ficha, pero el fichero sigue en Storage"
            ),
            "warning"
          );
        }
      }
      setToDelete(null);
    } catch (error) {
      toast(
        errorMessage(
          error,
          t("serviceBriefings.assets.deleteFailed", "No se pudo quitar")
        ),
        "error"
      );
    }
  };

  return (
    <Stack gap={2}>
      {assets.length === 0 && uploads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("serviceBriefings.assets.empty", "Todavía no hay assets.")}
        </Typography>
      ) : (
        <List dense disablePadding>
          {assets.map((asset) => (
            <ListItem
              key={asset.id}
              disableGutters
              secondaryAction={
                <Tooltip title={t("serviceBriefings.assets.remove", "Quitar")}>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setToDelete(asset)}
                    aria-label={t("serviceBriefings.assets.remove", "Quitar")}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {asset.kind === "LINK" ? (
                  <LinkIcon fontSize="small" />
                ) : (
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Link
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {asset.name}
                  </Link>
                }
                secondary={[
                  formatBytes(asset.sizeBytes),
                  asset.uploadedBy?.name,
                  new Date(asset.createdAt).toLocaleDateString("es-ES"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            </ListItem>
          ))}
          {uploads.map((upload) => (
            <ListItem key={upload.name} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AttachFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={upload.name}
                secondary={
                  <LinearProgress
                    variant="determinate"
                    value={upload.percent}
                    sx={{ mt: 0.5 }}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      <Box>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="service-briefing-file-input"
        />
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInput.current?.click()}
          disabled={uploads.length > 0}
        >
          {t("serviceBriefings.assets.upload", "Subir ficheros")}
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={0.5}
        >
          {t(
            "serviceBriefings.assets.maxSize",
            "Hasta {{mb}} MB por fichero. Lo que pese más, súbelo a Drive o WeTransfer y añade el enlace.",
            { mb: MAX_SERVICE_BRIEFING_FILE_BYTES / (1024 * 1024) }
          )}
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} gap={1}>
        <TextField
          size="small"
          label={t("serviceBriefings.assets.linkName", "Nombre del enlace")}
          value={linkName}
          onChange={(e) => setLinkName(e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label={t("serviceBriefings.assets.linkUrl", "URL")}
          placeholder="https://"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          sx={{ flex: 2 }}
        />
        <Button
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={handleAddLink}
          disabled={!linkValid || isAdding}
        >
          {t("serviceBriefings.assets.addLink", "Añadir enlace")}
        </Button>
      </Stack>

      <ConfirmationModal
        open={toDelete !== null}
        title={t("serviceBriefings.assets.removeTitle", "Quitar asset")}
        message={
          toDelete?.kind === "FILE"
            ? t(
                "serviceBriefings.assets.removeFileMessage",
                "Se quitará «{{name}}» de la ficha y se borrará el fichero.",
                { name: toDelete?.name }
              )
            : t(
                "serviceBriefings.assets.removeLinkMessage",
                "Se quitará el enlace «{{name}}» de la ficha.",
                { name: toDelete?.name }
              )
        }
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        isLoading={isDeleting}
      />
    </Stack>
  );
};

export default ServiceBriefingAssets;
